import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../../contexts/LanguageContext';

const MONTH_FIELDS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
];

const CollectionReportTable = ({ rows, grandTotals, loading }) => {
    const { th, getCurrentLocale } = useLanguage();
    const locale = getCurrentLocale ? getCurrentLocale() : 'tr-TR';

    const numberFormatter = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }),
        [locale]
    );

    const formatAmount = (value) => numberFormatter.format(Number(value) || 0);

    const tableRows = useMemo(
        () => (rows || []).map((r, i) => ({ ...r, _rowId: `${r.dealerCode}-${r.year}-${r.currency}-${i}` })),
        [rows]
    );

    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Yeni veri geldiğinde ilk sayfaya dön
    useEffect(() => {
        setFirst(0);
    }, [tableRows]);

    const onPage = (e) => {
        setFirst(e.first);
        setRowsPerPage(e.rows);
    };

    const amountBody = (rowData, options) => (
        <span className="block text-right">{formatAmount(rowData[options.field])}</span>
    );

    return (
        <div>
            <DataTable
                value={tableRows}
                dataKey="_rowId"
                loading={loading}
                emptyMessage={th('collectionReport.empty') || 'Kayıt bulunamadı'}
                scrollable
                scrollHeight="flex"
                size="small"
                stripedRows
                showGridlines
                paginator
                first={first}
                rows={rowsPerPage}
                onPage={onPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                currentPageReportTemplate="{first} - {last} / {totalRecords}"
            >
                <Column
                    field="dealerCode"
                    header={th('collectionReport.columns.dealerCode') || 'Bayi Kodu'}
                    frozen
                    style={{ minWidth: '8rem' }}
                />
                <Column
                    field="dealerName"
                    header={th('collectionReport.columns.dealerName') || 'Bayi Adı'}
                    frozen
                    style={{ minWidth: '16rem' }}
                />
                <Column field="year" header={th('collectionReport.columns.year') || 'Yıl'} style={{ minWidth: '6rem' }} />
                <Column
                    field="currency"
                    header={th('collectionReport.columns.currency') || 'Para Birimi'}
                    style={{ minWidth: '7rem' }}
                />
                <Column
                    field="total"
                    header={th('collectionReport.columns.total') || 'Toplam'}
                    body={amountBody}
                    style={{ minWidth: '10rem' }}
                    className="font-bold"
                />
                {MONTH_FIELDS.map((field) => (
                    <Column
                        key={field}
                        field={field}
                        header={th(`collectionReport.columns.${field}`)}
                        body={amountBody}
                        style={{ minWidth: '9rem' }}
                    />
                ))}
            </DataTable>

            {grandTotals && grandTotals.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                    {grandTotals.map((gt) => (
                        <div
                            key={gt.currency}
                            className="p-3 border-round surface-100 flex flex-column"
                            style={{ minWidth: '14rem' }}
                        >
                            <span className="text-sm text-color-secondary">
                                {th('collectionReport.grandTotal') || 'Genel Toplam'} ({gt.currency})
                            </span>
                            <span className="text-xl font-bold">
                                {formatAmount(gt.total)} {gt.currency}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CollectionReportTable;
